import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Target } from 'lucide-react';
import { goalsApi, type GoalProgress } from './goalsApi';
import { useEventMonitorSocket } from './useEventMonitorSocket';
import { queryKeys } from '@/lib/api/queryKeys';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { Badge } from '@/components/ui/Badge';
import { useT } from '@/lib/i18n/useT';

const STATUS_TONE: Record<GoalProgress['status'], 'success' | 'warn' | 'neutral'> = {
  met: 'success',
  'at-risk': 'warn',
  'on-track': 'neutral',
};

/**
 * Caso 12 → CH-08. Progreso de las metas del evento, en SÓLO LECTURA.
 *
 * Antes esta tarjeta también las editaba, lejos del sitio donde se planifica
 * el evento y sin saber nada del proyecto. Ahora se fijan en un solo lugar
 * (`GoalsEditor`: panel de propiedades del evento en el planner, y Plan del
 * proyecto) y aquí sólo se enseña cuánto se lleva y de dónde sale la meta.
 * El servidor calcula los actuales del mismo informe que `/xk-event/:id/report`.
 */
export function GoalProgressCard({ eventId, groupId }: { eventId: number; groupId?: number | null }) {
  const t = useT();
  const progressKey = useMemo(() => queryKeys.goals.progress(eventId), [eventId]);
  const progress = useQuery({
    queryKey: progressKey,
    queryFn: () => goalsApi.progress(eventId),
    enabled: Number.isFinite(eventId) && eventId > 0,
  });
  // Caso 12 v2 — con el gateway de Socket.IO vivo, las barras se refrescan en
  // segundos a cada `monitor:tick`; sin él, la tarjeta se queda con la caché.
  useEventMonitorSocket(eventId, progressKey as unknown as unknown[]);

  const data = progress.data ?? [];
  const planHref = groupId ? `/projects/${groupId}?tab=plan&event=${eventId}` : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Target className="h-4 w-4" /> {t('goals.title')}
        </CardTitle>
        <CardDescription>
          {t('goals.readOnlyHint')}{' '}
          {planHref ? (
            <Link to={planHref} className="font-semibold text-[var(--color-accent-ink)] hover:underline">
              {t('goals.editInPlan')}
            </Link>
          ) : null}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {progress.isLoading ? (
          <Skeleton className="h-20" />
        ) : data.length === 0 ? (
          <p className="text-sm text-[var(--color-fg-muted)]">{t('goals.empty')}</p>
        ) : (
          <ul className="space-y-3">
            {data.map((g) => (
              <li key={g.metric}>
                <div className="flex items-center justify-between gap-2 text-sm">
                  <span className="flex flex-wrap items-center gap-1.5 font-medium">
                    {t(`goals.metric.${g.metric}`)}
                    {g.source === 'project' && (
                      <span className="rounded-full border border-[var(--color-border-default)] px-1.5 text-[10px] font-bold uppercase tracking-wide text-[var(--color-fg-muted)]">
                        {t('goals.source.project')}
                      </span>
                    )}
                  </span>
                  <div className="flex items-center gap-2">
                    <Badge tone={STATUS_TONE[g.status]}>{t(`goals.status.${g.status}`)}</Badge>
                    <span className="tabular-nums text-xs text-[var(--color-fg-muted)]">
                      {g.actual} / {g.target}
                    </span>
                  </div>
                </div>
                <div className="mt-1 h-2 overflow-hidden rounded-full bg-[var(--color-bg-elevated)]">
                  <div
                    className={
                      'h-full rounded-full transition-all ' +
                      (g.status === 'met'
                        ? 'bg-[var(--color-success,#34D399)]'
                        : g.status === 'at-risk'
                          ? 'bg-[var(--color-warn,#FBBF24)]'
                          : 'bg-[var(--color-accent)]')
                    }
                    style={{ width: `${Math.min(100, g.percent * 100)}%` }}
                  />
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
